import {
  DashboardMarket,
  DashboardMarketAsset,
  PERCENT_ZERO,
} from "@silo/types";
import { ReactNode } from "react";
import { AssetContainer, DetailsContainer } from "./components";
import { PillContainer, PositionTypePill, TokenPill } from "./components/pills";
import {
  BalancePair,
  LiquidationPricePair,
  List,
  PointsPair,
} from "./components/list";
import { AprDisplayV2 } from "../apr-display-v2";
import { Array } from "effect";
import { getNativeRewardsSymbol } from "./utils";
import { DepositLiquidationPriceDescription } from "./components/deposit-liquidation-price-description";
import { DisabledCollateralWarning } from "../disabled-collateral-warning";
import { ActionContainer } from "./components/actions";

interface CollateralAssetProps {
  className?: string;
  market: DashboardMarket;
  asset: DashboardMarketAsset;
  tokenLogo: ReactNode;
  divider?: boolean;
  isLoan: boolean;
  depositMoreLink: ReactNode;
  withdrawLink: ReactNode;
}

export function CollateralAsset({
  className,
  market,
  asset,
  tokenLogo,
  divider,
  isLoan,
  depositMoreLink,
  withdrawLink,
}: CollateralAssetProps): JSX.Element {
  const {
    collateralBalance,
    collateralBalanceUsd,
    decimals,
    protocolKey,
    collateralPoints,
    depositLiquidationPriceUsd,
    isDisabledCollateral,
    priceNative,
    symbol,
  } = asset;

  const nativeRewardsSymbol = getNativeRewardsSymbol(protocolKey);

  const isPriceUnavailable = priceNative === 0n;

  return (
    <AssetContainer data-cy="deposit-asset-position">
      <DetailsContainer className={className}>
        <PillContainer>
          <TokenPill asset={asset} tokenLogo={tokenLogo} />

          <PositionTypePill>Borrowable deposit</PositionTypePill>
        </PillContainer>

        <List divider={divider}>
          <BalancePair
            label="Deposited"
            balance={collateralBalance}
            balanceUsd={isPriceUnavailable ? null : collateralBalanceUsd}
            decimals={decimals}
          />

          <AprDisplayV2.PositionCollateral
            asset={asset}
            nativeRewardsSymbol={nativeRewardsSymbol}
          />

          {Array.isNonEmptyReadonlyArray(collateralPoints) && (
            <PointsPair points={collateralPoints} />
          )}

          {isLoan && (
            <LiquidationPricePair
              liquidationPriceUsd={depositLiquidationPriceUsd}
              description={
                <DepositLiquidationPriceDescription
                  liquidationPriceUsd={depositLiquidationPriceUsd}
                />
              }
            />
          )}
        </List>
      </DetailsContainer>

      {divider && <div className="h-px bg-shale-700" />}

      {isDisabledCollateral && (
        <DisabledCollateralWarning assetSymbol={symbol} variant="panel" />
      )}

      <ActionContainer market={market} asset={asset}>
        {withdrawLink}

        {depositMoreLink}
      </ActionContainer>
    </AssetContainer>
  );
}
